import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { useBackground } from '@/lib/background/useBackground';
import { muestrasDeLaPaleta, tokensDesdeLaPaleta, type ThemeTokens } from '@/lib/background/paleta';
import { PATTERN_GROUPS } from '@/lib/background/patterns';
import type { BlendKey, GradientKey, HarmonyKey, PatternKey } from '@/lib/background/types';
import { uploadImage } from '@/lib/images/upload';
import { useT } from '@/lib/i18n/useT';

/**
 * Diseñar el fondo del evento, con el mismo motor que Xurvey.
 *
 * El generador —OKLCH, ocho políticas de color y 28 tramas— se trajo tal cual a
 * `@/lib/background`: es TypeScript puro sin más dependencia que React en su
 * hook, así que viaja entero. Lo que NO viaja es la interfaz: los dos productos
 * tienen su propia paleta de tokens y traer la de Xurvey serían dos sistemas de
 * estilo peleándose dentro del mismo diálogo.
 *
 * ## Qué se guarda: la imagen, no la receta
 *
 * Es la filosofía que hay que conservar, y la razón por la que duplicar el motor
 * es tolerable. El generador es reproducible por semilla y guardar su
 * configuración ocuparía cuarenta bytes en vez de trescientos kilos… pero
 * entonces el fondo dependería de que este código siga existiendo y pintando
 * exactamente igual dentro de dos años. **Un fondo es del evento, no del
 * generador**: se congela como imagen y deja de depender de nadie. Que Xotski y
 * Xurvey tengan cada uno su copia del motor no puede corromper un fondo ya
 * guardado, porque lo guardado no es código sino píxeles.
 *
 * ## Dónde acaba
 *
 * En los overrides del despliegue, como `backgroundImage: url("…")` — la misma
 * forma exacta que escribe el subidor de fichero de al lado. El runtime que lo
 * pinta sigue siendo el de Xurvey y no se entera de que lo generó Xotski, que
 * es justo el contrato del protocolo de integración: esta app produce
 * overrides, no dibuja encuestas.
 */
export function BackgroundStudioDialog({
  open,
  onOpenChange,
  onUse,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  /** La imagen lista para `backgroundImage`, y los colores si se pidieron. */
  onUse: (url: string, tokens: Partial<ThemeTokens> | null) => void;
}) {
  const t = useT();
  const { canvasRef, config, set, toDataUrl, toBlob, result } = useBackground({
    /*
     * Vertical, con la proporción del móvil donde va a acabar.
     *
     * El fondo se aplica con `cover` en un marco de teléfono: de una imagen
     * apaisada se recorta una franja central estrecha y esa franja se estira a
     * toda la altura, así que lo aprobado aquí no sería lo publicado.
     *
     * 1080×1920 y no 720×1280 porque un móvil de hoy pasa de mil píxeles de
     * ancho físico: a 720 el degradado se interpola al ampliarlo y se ve
     * lavado.
     */
    initialConfig: { width: 1080, height: 1920 },
  });
  const [guardando, setGuardando] = useState(false);
  /*
   * La paleta se aplica por defecto, y se puede no aplicar.
   *
   * Por defecto porque es lo que hace útil al generador: un fondo con los
   * colores anteriores encima no es un diseño, son dos peleando. Con
   * interruptor porque hay un caso real en contra —una marca con colores
   * cerrados que sólo quiere la textura— y a ésa, pisarle la paleta sin
   * preguntar sería destruirle trabajo.
   */
  const [aplicarPaleta, setAplicarPaleta] = useState(true);

  const propuesta = result ? tokensDesdeLaPaleta(result) : null;

  /*
   * WebP si el navegador sabe, PNG si no.
   *
   * `toDataURL` devuelve PNG en silencio cuando no conoce el formato pedido,
   * así que se comprueba el prefijo en vez de fiarse de que lo aceptó.
   */
  const exportar = () => {
    const webp = toDataUrl('image/webp', 0.92);
    if (webp.url.startsWith('data:image/webp')) return webp;
    return toDataUrl('image/png');
  };

  /*
   * Se sube como FICHERO y, si no se puede, se incrusta.
   *
   * La reserva no es adorno: si la subida falla —sin red, un proxy que corta
   * el multipart— quien acaba de diseñar un fondo se queda con él igual.
   * Perder cinco minutos de trabajo por un fallo de infraestructura es mucho
   * peor que unos kilobytes de más en los overrides.
   *
   * PNG al subir y WebP al incrustar, a propósito: como fichero el peso ya no
   * viaja en cada carga de la encuesta, así que se elige calidad; incrustado
   * hay que elegir tamaño.
   */
  const usar = async () => {
    setGuardando(true);
    const tokens = aplicarPaleta && propuesta ? propuesta.tokens : null;
    try {
      const blob = await toBlob('image/png');
      if (blob) {
        const file = new File([blob], `fondo-${config.seed}.png`, { type: 'image/png' });
        // `compress: false`: el PNG se eligió a conciencia por no tener bandas
        // en el degradado, y recomprimirlo aquí desharía justo eso.
        const { url } = await uploadImage(file, { compress: false });
        onUse(url, tokens);
        onOpenChange(false);
        setGuardando(false);
        return;
      }
    } catch {
      /* se sigue por la reserva de abajo */
    }
    const { url } = exportar();
    if (url) onUse(url, tokens);
    onOpenChange(false);
    setGuardando(false);
  };

  const salida = result ? exportar() : null;
  const pesoAprox = salida ? Math.round(salida.kb) : 0;
  const formato = salida?.url.startsWith('data:image/webp') ? 'WebP' : 'PNG';
  const avisos = (result?.audit ?? []).filter((a) => a.state !== 'ok');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[900px]">
        <DialogHeader>
          <DialogTitle>{t('bgStudio.title')}</DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-4 lg:flex-row">
          <div className="flex min-w-0 flex-1 flex-col gap-2">
            <canvas
              // React 18 todavía no acepta un ref que admita null en su tipo; el
              // hook lo declara así porque el lienzo no existe hasta el montaje.
              ref={canvasRef as React.RefObject<HTMLCanvasElement>}
              className="mx-auto rounded-lg border border-[var(--color-xk-border)]"
              // La misma silueta que el móvil: una previa apaisada de un destino
              // vertical aprueba algo distinto de lo que se publica.
              style={{ aspectRatio: '9 / 16', maxHeight: '46vh', width: 'auto' }}
            />

            {/* La auditoría del motor, sólo cuando corrigió o avisó. Una lista
                de ocho «ok» es ruido que enseña a no leerla. */}
            {avisos.length > 0 ? (
              <ul className="flex flex-col gap-0.5">
                {avisos.map((a) => (
                  <li
                    key={a.title}
                    className="text-[10px] leading-snug text-[var(--color-xk-text-muted)]"
                  >
                    <span style={{ color: a.state === 'warn' ? 'var(--color-warn)' : 'var(--color-xk-accent)' }}>
                      {a.state === 'warn' ? '⚠' : '✎'}
                    </span>{' '}
                    {a.title} · {a.detail}
                  </li>
                ))}
              </ul>
            ) : null}

            {/* La paleta propuesta, con su reparto. Va debajo del lienzo y no
                plegada porque es la mitad del resultado: quien elige un fondo
                está eligiendo a la vez los colores, y verlos antes de aplicar
                evita volver al panel y encontrarlo todo cambiado. */}
            {result ? (
              <>
                <div className="flex overflow-hidden rounded-lg border border-[var(--color-xk-border)]">
                  {muestrasDeLaPaleta(result).map((m) => (
                    <div
                      key={m.rol}
                      className="flex min-w-0 flex-1 flex-col gap-1.5 px-2 py-2"
                      style={{ background: m.hex, color: m.tinta }}
                    >
                      <span className="truncate text-[9px] uppercase tracking-[0.14em] opacity-80">
                        {m.rol} · {m.peso}
                      </span>
                      <span className="font-mono text-[10px]">{m.hex}</span>
                      {m.ajustado ? (
                        <span className="text-[9px] leading-tight opacity-75">
                          {t('bgStudio.lightened')}
                        </span>
                      ) : null}
                    </div>
                  ))}
                </div>

                <div className="flex flex-wrap gap-x-4 gap-y-1 text-[10px] text-[var(--color-xk-text-muted)]">
                  <Metrica etiqueta={t('bgStudio.textContrast')}>
                    <b
                      className="font-mono"
                      style={{ color: result.ratio >= 4.5 ? 'var(--color-success)' : 'var(--color-warn)' }}
                    >
                      {result.ratio.toFixed(2)}:1
                    </b>
                  </Metrica>
                  {/* El del botón contra el fondo decide si la llamada a la
                      acción se ve. 3:1 es lo que pide la WCAG para un componente
                      de interfaz, no los 4.5 del texto. */}
                  <Metrica etiqueta={t('bgStudio.buttonContrast')}>
                    <b
                      className="font-mono"
                      style={{
                        color:
                          (propuesta?.ratioBotonFondo ?? 0) >= 3
                            ? 'var(--color-success)'
                            : 'var(--color-warn)',
                      }}
                    >
                      {(propuesta?.ratioBotonFondo ?? 0).toFixed(2)}:1
                    </b>
                  </Metrica>
                  <Metrica etiqueta={t('bgStudio.harmony')}>
                    <b>{result.palette.harmony}</b>
                  </Metrica>
                </div>
              </>
            ) : null}
          </div>

          <div className="flex w-full flex-none flex-col gap-2.5 lg:w-[240px]">
            <Campo label={`${t('bgStudio.hue')} · ${config.hue}°`}>
              <Rango value={config.hue} max={359} onChange={(v) => set('hue', v)} />
            </Campo>

            <Campo label={t('bgStudio.harmony')}>
              <Selector
                value={config.harmony}
                onChange={(v) => set('harmony', v as HarmonyKey)}
                opciones={ARMONIAS}
              />
            </Campo>

            <Campo label={t('bgStudio.gradient')}>
              <Selector
                value={config.gtype}
                onChange={(v) => set('gtype', v as GradientKey)}
                opciones={DEGRADADOS}
              />
            </Campo>

            <Campo label={`${t('bgStudio.angle')} · ${config.angle}°`}>
              <Rango value={config.angle} max={359} onChange={(v) => set('angle', v)} />
            </Campo>

            <Campo label={t('bgStudio.pattern')}>
              <select
                value={config.pattern}
                onChange={(e) => set('pattern', e.target.value as PatternKey)}
                className={INPUT}
              >
                <option value="none">{t('bgStudio.noPattern')}</option>
                {PATTERN_GROUPS.map(([grupo, tramas]) => (
                  <optgroup key={grupo} label={grupo}>
                    {tramas.map(([key, label]) => (
                      <option key={key} value={key}>
                        {label}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>
            </Campo>

            {config.pattern !== 'none' ? (
              <>
                <Campo label={`${t('bgStudio.patternSize')} · ${config.pscale}px`}>
                  <Rango
                    value={config.pscale}
                    min={14}
                    max={120}
                    onChange={(v) => set('pscale', v)}
                  />
                </Campo>
                <Campo
                  label={`${t('bgStudio.patternOpacity')} · ${Math.round(config.popacity * 100)}%`}
                >
                  <Rango
                    value={Math.round(config.popacity * 100)}
                    max={100}
                    onChange={(v) => set('popacity', v / 100)}
                  />
                </Campo>
                <Campo label={t('bgStudio.blend')}>
                  <Selector
                    value={config.blend}
                    onChange={(v) => set('blend', v as BlendKey)}
                    opciones={FUSIONES}
                  />
                </Campo>
              </>
            ) : null}

            <div className="flex flex-col gap-1">
              <Casilla
                label={t('bgStudio.vignette')}
                on={config.vignette}
                onChange={(v) => set('vignette', v)}
              />
              <Casilla label={t('bgStudio.glow')} on={config.glow} onChange={(v) => set('glow', v)} />
              <Casilla label={t('bgStudio.grain')} on={config.grain} onChange={(v) => set('grain', v)} />
            </div>

            <Button
              variant="outline"
              size="sm"
              onClick={() => set('seed', Math.floor(Math.random() * 1e6))}
            >
              ↻ {t('bgStudio.another')}
            </Button>
          </div>
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-3 border-t border-[var(--color-xk-border)] pt-3">
          <span className="text-[10px] text-[var(--color-xk-text-muted)]">
            {t('bgStudio.savedAs', { format: formato, kb: pesoAprox })}
          </span>
          <label className="flex cursor-pointer items-center gap-1.5 text-[11px] text-[var(--color-xk-lavender)]">
            <input
              type="checkbox"
              checked={aplicarPaleta}
              onChange={() => setAplicarPaleta((v) => !v)}
              className="h-3.5 w-3.5 accent-[var(--color-xk-accent)]"
            />
            {t('bgStudio.applyPalette')}
          </label>
          <div className="ml-auto flex gap-2">
            <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={guardando}>
              {t('stock.dialog.cancel')}
            </Button>
            <Button onClick={() => void usar()} disabled={guardando || !result}>
              {guardando ? t('bgStudio.applying') : t('bgStudio.use')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------

const ARMONIAS: Array<[HarmonyKey, string]> = [
  ['mono', 'Monocromo'],
  ['analogous', 'Análogos'],
  ['complementary', 'Complementarios'],
  ['split', 'Complementarios partidos'],
  ['triadic', 'Tríada'],
  ['tetradic', 'Tétrada'],
];

const DEGRADADOS: Array<[GradientKey, string]> = [
  ['linear', 'Lineal'],
  ['radial', 'Radial'],
  ['conic', 'Cónico'],
  ['mesh', 'Malla'],
  ['bands', 'Bandas'],
];

const FUSIONES: Array<[BlendKey, string]> = [
  ['soft-light', 'Luz suave'],
  ['overlay', 'Superponer'],
  ['multiply', 'Multiplicar'],
  ['screen', 'Trama'],
  ['source-over', 'Normal'],
];

const INPUT =
  'w-full rounded-md border border-[var(--color-xk-border)] bg-[var(--color-xk-bg-deep)] px-2 py-1.5 text-xs text-[var(--color-xk-lavender)] focus:border-[var(--color-accent-ink)] focus:outline-none';

function Campo({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-[10px] font-bold uppercase tracking-widest text-[var(--color-xk-text-muted)]">
        {label}
      </span>
      {children}
    </label>
  );
}

function Rango({
  value,
  min = 0,
  max,
  onChange,
}: {
  value: number;
  min?: number;
  max: number;
  onChange: (v: number) => void;
}) {
  return (
    <input
      type="range"
      min={min}
      max={max}
      value={value}
      onChange={(e) => onChange(Number(e.target.value))}
      className="w-full accent-[var(--color-xk-accent)]"
    />
  );
}

function Selector({
  value,
  onChange,
  opciones,
}: {
  value: string;
  onChange: (v: string) => void;
  opciones: Array<[string, string]>;
}) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} className={INPUT}>
      {opciones.map(([k, label]) => (
        <option key={k} value={k}>
          {label}
        </option>
      ))}
    </select>
  );
}

function Casilla({
  label,
  on,
  onChange,
}: {
  label: string;
  on: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label className="flex cursor-pointer items-center gap-1.5 text-[11px] text-[var(--color-xk-lavender)]">
      <input
        type="checkbox"
        checked={on}
        onChange={(e) => onChange(e.target.checked)}
        className="h-3.5 w-3.5 accent-[var(--color-xk-accent)]"
      />
      {label}
    </label>
  );
}

const Metrica = ({ etiqueta, children }: { etiqueta: string; children: React.ReactNode }) => (
  <span>
    {etiqueta} {children}
  </span>
);
